
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PlaatoSignalRService } from 'app/services/external/plaato-signalr.service';
import * as PlaatoActions from './actions';
import { catchError, map, mergeMap, tap, switchMap, withLatestFrom } from 'rxjs/operators';
import { of, merge, from, EMPTY } from 'rxjs';
import { environment } from 'environments/environment';

import { SignalRConnectionState } from 'app/models/plaato-message.model';

@Injectable()
export class PlaatoStatusEffects {

  private actions$ = inject(Actions);
  private signalRService = inject(PlaatoSignalRService);


  startConnection$ = createEffect(() => this.actions$.pipe(
    ofType(PlaatoActions.startConnection),
    switchMap(() => {
      const hubUrl = `${environment.plaatoApiServer}/messagehistory`;
      return from(this.signalRService.connect(hubUrl)).pipe(
        // Don't dispatch - service emits state changes via connectionState$
        switchMap(() => EMPTY),
        catchError(error =>
          of(PlaatoActions.errorOccurred({ error: error.message }))
        )
      );
    })
  ));

  // Listen to connection state changes
  connectionState$ = createEffect(() => this.signalRService.connectionState$.pipe(
    map((state: SignalRConnectionState) => PlaatoActions.connectionStateChanged({ state }))
  ));

  // Listen to messages
  messageUpdate$ = createEffect(() => this.signalRService.messageUpdate$.pipe(
    map(({ deviceId, message }) => PlaatoActions.messageReceived({ deviceId, message }))
  ));

  // Listen to history
  historyReceived$ = createEffect(() => this.signalRService.history$.pipe(
    map(({ deviceId, history }) => PlaatoActions.historyReceived({ deviceId, history }))
  ));

  // Listen to device list
  deviceListReceived$ = createEffect(() => this.signalRService.deviceList$.pipe(
    map((devices: string[]) => PlaatoActions.deviceListReceived({ devices }))
  ));

  // Listen to errors
  error$ = createEffect(() => this.signalRService.error$.pipe(
    map((error: string) => PlaatoActions.errorOccurred({ error }))
  ));

  // Subscribe to device - only subscribe when connected
  subscribeToDevice$ = createEffect(() => this.actions$.pipe(
    ofType(PlaatoActions.subscribeToDevice),
    withLatestFrom(
      this.signalRService.connectionState$
    ),
    switchMap(([{ deviceId }, connectionState]) => {
      // Only proceed if connected
      if (connectionState !== SignalRConnectionState.Connected) {
        return EMPTY; // Wait for connection
      }

      return from(this.signalRService.subscribeToDevice(deviceId)).pipe(
        map(() => ({ type: '[Plaato Realtime] Subscribe Success' })),
        catchError((error: any) => of(PlaatoActions.errorOccurred({ error: error.message })))
      );
    })
  ));

  // Unsubscribe
  unsubscribeFromDevice$ = createEffect(() => this.actions$.pipe(
    ofType(PlaatoActions.unsubscribeFromDevice),
    mergeMap(({ deviceId }) =>
      from(this.signalRService.unsubscribeFromDevice(deviceId)).pipe(
        map(() => ({ type: 'NOOP' })),
        catchError((error: any) => {
          // Log error but don't fail
          return of(PlaatoActions.errorOccurred({ error: 'Failed to unsubscribe: ' + error.message }));
        })
      )
    )
  ), { dispatch: false });

  // Load history automatically when subscribed
  loadHistory$ = createEffect(() => this.actions$.pipe(
    ofType(PlaatoActions.subscribeToDevice),
    mergeMap(({ deviceId }) =>
      // We use getHistory which invokes the hub method.
      // The service receives the data and emits on history$.
      // Our historyReceived$ effect above will catch it and dispatch the action.
      from(this.signalRService.getHistory(deviceId)).pipe(
        map(() => ({ type: 'NOOP' })),
        catchError((error: any) => {
          // If history fetch fails, just log the error - messages will still come via MessageUpdate
          return of(PlaatoActions.errorOccurred({ error: 'Failed to load history: ' + error.message }));
        })
      )
    )
  ), { dispatch: false });
}
